import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type OverlayState = 'closed' | 'open' | 'closing'

/**
 * Manages detail overlay lifecycle with enter/exit animations
 * and scroll preservation for main content and global search.
 */
export function useDetailOverlay(isOpen: boolean) {
  const mainScrollRef = useRef(0)
  const gsScrollRef = useRef(0)
  const [state, setState] = useState<OverlayState>('closed')

  useLayoutEffect(() => {
    if (!isOpen) return
    const main = document.getElementById('main-content')
    const gsOverlay = document.getElementById('global-search-overlay')
    if (main) mainScrollRef.current = main.scrollTop
    if (gsOverlay) gsScrollRef.current = gsOverlay.scrollTop
    setState('open')
  }, [isOpen])

  // Close with animation delay
  useEffect(() => {
    if (isOpen) return
    if (state === 'closed') return

    setState('closing')
    const timer = setTimeout(() => {
      setState('closed')
      const main = document.getElementById('main-content')
      if (main) main.scrollTop = mainScrollRef.current
      const gs = document.getElementById('global-search-overlay')
      if (gs) gs.scrollTop = gsScrollRef.current
    }, 150)

    return () => clearTimeout(timer)
  }, [isOpen, state])

  return state
}

/**
 * Renders children into the detail overlay portal (above main and global search).
 */
export function renderDetailOverlay(
  state: OverlayState,
  children: React.ReactNode,
) {
  if (state === 'closed') return null
  const target = document.getElementById('detail-overlay-root')
  if (!target) return null
  return createPortal(
    <div
      className={`absolute inset-x-0 top-14 bottom-0 z-30 overflow-x-hidden overflow-y-auto border-t border-gray-200 bg-warm-25 px-4 pt-4 pb-20 ${
        state === 'closing' ? 'animate-detail-out' : 'animate-detail-in'
      }`}
    >
      <div className="mx-auto min-w-0 max-w-screen-3xl">{children}</div>
    </div>,
    target,
  )
}
